import {
  getDragCustomerName,
  getDragPreviewMessage,
  getPointerClientY,
  isSaleAssignPayload,
  SaleAssignDragPayload,
  setSaleAssignDrag,
} from '@/pages/sale/lib/sale-comment-drag';
import { snapCenterToCursor } from '@/pages/sale/lib/sale-assign-dnd-modifiers';
import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type SaleAssignDndContextValue = {
  pointerClientY: number | null;
  isDragging: boolean;
};

const SaleAssignDndContext = createContext<SaleAssignDndContextValue>({
  pointerClientY: null,
  isDragging: false,
});

export function useSaleAssignDnd() {
  return useContext(SaleAssignDndContext);
}

function AssignDragOverlay({ payload }: { payload: SaleAssignDragPayload | null }) {
  if (!payload) return null;

  const name = getDragCustomerName(payload);
  const message = getDragPreviewMessage(payload);

  return (
    <div className="pointer-events-none rounded-[10px] bg-[#1877F2] px-3 py-2 text-white shadow-lg max-w-[220px]">
      <div className="text-xs font-semibold leading-snug">{name}</div>
      {message?.trim() && (
        <div className="mt-0.5 truncate text-[11px] font-medium opacity-90">{message.trim()}</div>
      )}
    </div>
  );
}

export function SaleAssignDndProvider({ children }: { children: React.ReactNode }) {
  const [activePayload, setActivePayload] = useState<SaleAssignDragPayload | null>(null);
  const [pointerClientY, setPointerClientY] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const updatePointerY = useCallback((event: DragMoveEvent | DragEndEvent) => {
    const clientY = getPointerClientY(event.activatorEvent, event.delta.y);
    setPointerClientY(clientY);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const payload = event.active.data.current as SaleAssignDragPayload | undefined;
    if (!payload || !isSaleAssignPayload(payload)) return;
    setActivePayload(payload);
    setSaleAssignDrag(payload);
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      updatePointerY(event);
    },
    [updatePointerY],
  );

  const clearDragState = useCallback(() => {
    setActivePayload(null);
    setPointerClientY(null);
    setSaleAssignDrag(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      updatePointerY(event);
      clearDragState();
    },
    [clearDragState, updatePointerY],
  );

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      clearDragState();
    },
    [clearDragState],
  );

  const contextValue = useMemo(
    () => ({
      pointerClientY,
      isDragging: !!activePayload,
    }),
    [activePayload, pointerClientY],
  );

  return (
    <SaleAssignDndContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
          <AssignDragOverlay payload={activePayload} />
        </DragOverlay>
      </DndContext>
    </SaleAssignDndContext.Provider>
  );
}
